import { 
  TrendingUp, 
  DollarSign, 
  Users,
  BarChart3,
  Shield,
  ClipboardList,
  Lock
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface DashboardPageProps {
  onNavigate: (section: string) => void;
}

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  const cards = [
    {
      id: 1,
      title: 'Gross Profit Analytics',
      description: 'Track and analyze your profit margins',
      icon: DollarSign,
      section: 'gross-profit',
      enabled: true,
      size: 'large', // 2x2
      gradient: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-100 dark:bg-emerald-950',
      iconColor: 'text-emerald-600 dark:text-emerald-400'
    },
    {
      id: 2,
      title: 'VIP Analytics',
      description: 'View VIP analytics and engagement insights',
      icon: TrendingUp,
      section: 'game-master-analytica',
      enabled: true,
      size: 'medium-v', // 1x2
      gradient: 'from-blue-500 to-indigo-600',
      iconBg: 'bg-blue-100 dark:bg-blue-950',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      id: 3,
      title: 'Earn Analytics',
      description: 'Monitor allocation and rebate trends',
      icon: BarChart3,
      section: 'earn-analytics',
      enabled: true,
      size: 'small', // 1x1
      gradient: 'from-amber-500 to-orange-600',
      iconBg: 'bg-amber-100 dark:bg-amber-950',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    {
      id: 4,
      title: 'Customer Insights',
      description: 'Coming soon',
      icon: Users,
      section: null,
      enabled: false,
      size: 'small', // 1x1
      gradient: 'from-gray-400 to-gray-500',
      iconBg: 'bg-gray-100 dark:bg-gray-900',
      iconColor: 'text-gray-400 dark:text-gray-600'
    },
    {
      id: 5,
      title: 'VIP Logger',
      description: 'Track VIP logs and event details',
      icon: ClipboardList,
      section: 'vip-logger',
      enabled: true,
      size: 'medium-h', // 2x1
      gradient: 'from-violet-500 to-purple-600',
      iconBg: 'bg-violet-100 dark:bg-violet-950',
      iconColor: 'text-violet-600 dark:text-violet-400'
    },
    {
      id: 6,
      title: 'Game Master Analytica',
      description: 'Analyze missions and engagement trends',
      icon: Shield,
      section: 'game-master-analytica',
      enabled: true,
      size: 'small', // 1x1
      gradient: 'from-cyan-500 to-sky-600',
      iconBg: 'bg-cyan-100 dark:bg-cyan-950',
      iconColor: 'text-cyan-600 dark:text-cyan-400'
    }
  ];

  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'large':
        return 'col-span-2 row-span-2';
      case 'medium-h':
        return 'col-span-2 row-span-1';
      case 'medium-v':
        return 'col-span-1 row-span-2';
      case 'small':
      default:
        return 'col-span-1 row-span-1';
    }
  };

  const handleCardClick = (card: typeof cards[0]) => {
    if (card.enabled && card.section) {
      onNavigate(card.section);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground">
            Welcome back! Here's an overview of your business analytics.
          </p>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[240px]">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.id}
              className={`
                ${getSizeClasses(card.size)}
                relative overflow-hidden border-2 transition-all duration-300
                ${
                  card.enabled
                    ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg border-border hover:border-primary/50'
                    : 'opacity-60 cursor-not-allowed border-border/50'
                }
              `}
              onClick={() => handleCardClick(card)}
            >
              {/* Background Gradient Orb */}
              <div
                className={`
                  absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20
                  bg-gradient-to-br ${card.gradient}
                  ${!card.enabled && 'opacity-10'}
                `}
              />

              {/* Content */}
              <div className="relative h-full p-6 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-xl ${card.iconBg}`}>
                      <Icon className={`h-6 w-6 ${card.iconColor}`} />
                    </div>
                    {!card.enabled && (
                      <Badge variant="secondary" className="gap-1">
                        <Lock className="h-3 w-3" />
                        Soon
                      </Badge>
                    )}
                  </div>

                  <div>
                    <h3 className={card.enabled ? '' : 'text-muted-foreground'}>
                      {card.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {card.description}
                    </p>
                  </div>
                </div>

                {/* Action Indicator */}
                {card.enabled && (
                  <div className="flex items-center gap-2 text-sm text-primary transition-transform">
                    View Details
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

